import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Users, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import educationPattern from "@/assets/education-pattern.webp";


// Apple-inspired easing
const appleEase: [number, number, number, number] = [0.22, 1, 0.36, 1];
const duration = 0.8;
const stagger = 0.1;

const featurePoints = [
  {
    icon: Sparkles,
    title: "Shape new features",
    description: "Your ideas help us build what matters most to students",
  },
  {
    icon: Users,
    title: "Help other Tasmanian students",
    description: "Your experience can guide improvements for everyone",
  },
  {
    icon: Heart,
    title: "Your feedback matters",
    description: "We read every response and take action on your suggestions",
  },
];

function FeaturePoint({ icon: Icon, title, description }: { 
  icon: typeof Sparkles; 
  title: string; 
  description: string;
}) {
  return (
    <div className="flex gap-4 items-start">
      <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-accent" />
      </div>
      <div>
        <h4 className="font-semibold text-foreground">{title}</h4>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

export function FeedbackSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: '-80px' });

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: stagger,
      },
    },
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 8 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: duration * 0.6, ease: appleEase } 
    },
  };

  const slideUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration, ease: appleEase } 
    },
  };

  const lineReveal = {
    hidden: { scaleX: 0, originX: 0 },
    visible: { 
      scaleX: 1, 
      transition: { duration: duration * 0.7, ease: appleEase } 
    },
  };

  return (
    <section
      ref={ref}
      className="relative py-24 md:py-32 lg:py-40 overflow-hidden bg-background text-foreground"
    >
      <div
        className="absolute inset-0 z-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage: `url(${educationPattern})`,
          backgroundSize: '800px',
          backgroundRepeat: 'repeat',
        }}
      />
      
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="grid gap-12 lg:gap-20 lg:grid-cols-2 items-center">
          {/* Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="max-w-2xl"
          >
            {/* Eyebrow */}
            <motion.p
              variants={fadeIn}
              className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase mb-4 text-muted-foreground"
            >
              Your Voice Matters
            </motion.p>

            {/* Headline */}
            <motion.h2
              variants={slideUp}
              className="font-display text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight leading-[1.08] mb-4"
            >
              We Want to Hear From You
            </motion.h2>

            {/* Divider Line with gradient */}
            <motion.div
              variants={lineReveal}
              className="h-[2px] w-16 mb-6 bg-gradient-to-r from-foreground/30 to-transparent"
            />

            {/* Description */}
            <motion.p
              variants={slideUp}
              className="text-base md:text-lg lg:text-xl leading-[1.6] mb-8 text-muted-foreground"
            >
              A platform built by Tasmanians for Tasmanians, your feedback helps us 
              create the best possible learning experience for students across the state.
            </motion.p>

            {/* Feature Points */}
            <motion.div variants={slideUp} className="space-y-6 mb-8">
              {featurePoints.map((point, index) => (
                <FeaturePoint
                  key={index}
                  icon={point.icon}
                  title={point.title}
                  description={point.description}
                />
              ))}
            </motion.div>

            {/* CTA Button */}
            <motion.div variants={slideUp}>
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/feedback">
                  We want to hear your experience
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Editorial quote card */}
          <motion.div
            initial={{ opacity: 0, x: 40, scale: 0.98 }}
            animate={isInView ? { opacity: 1, x: 0, scale: 1 } : { opacity: 0, x: 40, scale: 0.98 }}
            transition={{ duration: duration * 1.3, ease: appleEase, delay: stagger * 2 }}
            className="relative flex items-center justify-center"
          >
            <figure className="w-full max-w-md rounded-3xl border border-border/60 bg-card/70 backdrop-blur-xl p-10 shadow-xl">
              <span aria-hidden className="block font-serif italic text-7xl leading-none text-accent mb-4">“</span>
              <blockquote className="font-display text-2xl md:text-3xl font-semibold leading-snug">
                Every change we make starts with what students and parents tell us.
              </blockquote>
              <div className="h-px w-12 bg-accent my-6" />
              <figcaption className="text-sm text-muted-foreground">
                Gilbert Loane, Founder · UnlockEd Tutoring
              </figcaption>
            </figure>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
